import React, { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Loader2, RefreshCw, Eye, Search, CircleDashed, Copy, Download, Receipt, ChevronUp, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import PageHeader from '../components/PageHeader';
import StatusBadge from '../components/StatusBadge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '../components/ui/sheet';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import EmptyState from '../components/EmptyState';
import { api } from '../lib/api';
import { useLang } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency, formatDate, shortId } from '../lib/utils';

// CANCELLED visibility: cancelled_skipped (and, defensively, any
// non-processed order whose TikTok raw_status is CANCELLED) shows as
// "Cancelled" — never as Pending.
const displayStatus = (o) => {
  // A synced order (Estimate created) later cancelled on TikTok keeps a
  // distinct badge so the operator sees it at a glance without opening QBO.
  if (o?.post_sync_cancelled) return 'cancelled_after_sync';
  if (
    o?.status === 'cancelled_skipped' ||
    (o?.raw?.raw_status === 'CANCELLED' && o?.status !== 'processed')
  ) return 'cancelled';
  return o?.status;
};

// Copies text to the clipboard, falling back to a hidden textarea +
// execCommand when the async Clipboard API is unavailable/blocked
// (older browsers, insecure contexts, restricted iframes).
function copyToClipboard(text, successMessage) {
  const fallback = () => {
    try {
      const el = document.createElement('textarea');
      el.value = text;
      el.style.position = 'fixed';
      el.style.opacity = '0';
      document.body.appendChild(el);
      el.focus();
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      toast.success(successMessage);
    } catch (err) {
      toast.error('No se pudo copiar');
    }
  };
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(text).then(
      () => toast.success(successMessage),
      fallback,
    );
  } else {
    fallback();
  }
}

export default function OrdersPage() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const locale = lang === 'es' ? 'es' : 'en-US';
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [status, setStatus] = useState('all');
  const [q, setQ] = useState('');
  const [auction, setAuction] = useState('');
  const [selected, setSelected] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [sortBy, setSortBy] = useState('date');
  const [sortDir, setSortDir] = useState('desc');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const toggleSort = (col) => {
    if (sortBy === col) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(col);
      setSortDir('desc');
    }
    setPage(1);
  };

  // Deep-link support: /orders?focus=TT-12345 will pre-fill the search
  // and auto-open the detail drawer once the row is found.
  useEffect(() => {
    const focus = searchParams.get('focus');
    if (focus && !focus.startsWith('customer:')) {
      setQ(focus);
      setSelected(focus);
    }
  }, [searchParams]);

  const ordersQuery = useQuery({
    queryKey: ['orders', { page, pageSize, status, q, auction, sortBy, sortDir, fromDate, toDate }],
    queryFn: async () => {
      const params = { page, page_size: pageSize, sort_by: sortBy, sort_dir: sortDir };
      if (status && status !== 'all') params.status = status;
      if (q) params.q = q;
      if (auction) params.auction = auction.trim();
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      return (await api.get('/orders', { params })).data;
    },
  });

  const detailQuery = useQuery({
    queryKey: ['orders', 'detail', selected],
    enabled: !!selected,
    queryFn: async () => (await api.get(`/orders/${selected}`)).data,
  });

  const retryMutation = useMutation({
    mutationFn: async (id) => (await api.post(`/orders/${id}/retry`)).data,
    onSuccess: () => {
      toast.success('Reintento enviado');
      qc.invalidateQueries({ queryKey: ['orders'] });
    },
    onError: () => toast.error('No se pudo reintentar'),
  });

  const looksLikeOrderId = q && /^[A-Za-z0-9_-]{8,}$/.test(q.trim());

  const handleSyncFromTikTok = async () => {
    if (!window.confirm(t('search.sync_confirm_body'))) return;
    try {
      setSyncing(true);
      const { data } = await api.post('/tiktok/sync/orders', null, {
        params: { hours_back: 24, max_pages: 5, page_size: 50 },
      });
      const pulled = data?.pulled ?? 0;
      const processed = data?.processed ?? 0;
      toast.success(
        t('search.sync_done')
          .replace('{{pulled}}', pulled)
          .replace('{{processed}}', processed)
      );
      qc.invalidateQueries({ queryKey: ['orders'] });
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || '';
      toast.error(`${t('search.sync_error')}${detail ? ` — ${detail}` : ''}`);
    } finally {
      setSyncing(false);
    }
  };

  const data = ordersQuery.data || { items: [], total: 0 };
  const totalPages = Math.max(1, Math.ceil(data.total / pageSize));

  const handleExport = async () => {
    try {
      setExporting(true);
      const params = { sort_by: sortBy, sort_dir: sortDir };
      if (status && status !== 'all') params.status = status;
      if (q) params.q = q;
      if (auction) params.auction = auction.trim();
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      const res = await api.get('/orders/export', { params, responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const today = new Date().toISOString().slice(0, 10);
      const a = document.createElement('a');
      a.href = url;
      a.download = `orders_${today}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t('orders.export_done'));
    } catch (err) {
      toast.error(t('orders.export_error'));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div data-testid="orders-page" className="animate-fade-slide-up">
      <PageHeader
        title={t('orders.title')}
        subtitle={t('orders.subtitle')}
        actions={
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="default"
              onClick={handleExport}
              disabled={exporting}
              data-testid="orders-export-button"
            >
              {exporting
                ? <Loader2 className="h-[14px] w-[14px] animate-spin" />
                : <Download className="h-[14px] w-[14px]" />}
              {t('orders.export')}
            </Button>
            <Button
              variant="secondary"
              size="default"
              onClick={() => qc.invalidateQueries({ queryKey: ['orders'] })}
              data-testid="orders-refresh-button"
            >
              <RefreshCw className="h-[14px] w-[14px]" /> {t('common.refresh')}
            </Button>
          </div>
        }
      />

      {/* Toolbar */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:gap-3 mb-4">
        <div className="relative w-full md:w-80">
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground/60 pointer-events-none"
            strokeWidth={1.75}
          />
          <Input
            placeholder={t('orders.search_placeholder')}
            value={q}
            onChange={(e) => { setQ(e.target.value); setPage(1); }}
            className="pl-9"
            data-testid="orders-search-input"
          />
        </div>
        <div className="w-full md:w-40">
          <Input
            placeholder={t('orders.filter_auction')}
            value={auction}
            onChange={(e) => { setAuction(e.target.value); setPage(1); }}
            data-testid="orders-auction-filter-input"
          />
        </div>
        <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}>
          <SelectTrigger
            className="w-44 h-[38px] rounded-[10px] bg-[hsl(var(--surface-2))] text-[13px] text-muted-foreground transition-colors"
            style={{ borderColor: 'var(--border-default)' }}
            data-testid="orders-status-filter"
          >
            <SelectValue placeholder={t('orders.filter_status')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('common.filter')} —</SelectItem>
            <SelectItem value="processed">{t('status.processed')}</SelectItem>
            <SelectItem value="pending">{t('status.pending')}</SelectItem>
            <SelectItem value="failed">{t('status.failed')}</SelectItem>
            <SelectItem value="cancelled">{t('status.cancelled')}</SelectItem>
            <SelectItem value="refunded">{t('status.refunded')}</SelectItem>
            <SelectItem value="manual_review">{t('status.manual_review')}</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2">
          <Input
            type="date"
            aria-label={t('orders.filter_date_from')}
            title={t('orders.filter_date_from')}
            value={fromDate}
            onChange={(e) => { setFromDate(e.target.value); setPage(1); }}
            className="w-[150px] h-[38px] text-[13px]"
            data-testid="orders-date-from"
          />
          <span className="text-muted-foreground text-[12px]">–</span>
          <Input
            type="date"
            aria-label={t('orders.filter_date_to')}
            title={t('orders.filter_date_to')}
            value={toDate}
            onChange={(e) => { setToDate(e.target.value); setPage(1); }}
            className="w-[150px] h-[38px] text-[13px]"
            data-testid="orders-date-to"
          />
        </div>
        {(q || auction || (status && status !== 'all') || fromDate || toDate) && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { setQ(''); setAuction(''); setStatus('all'); setFromDate(''); setToDate(''); setPage(1); }}
            data-testid="orders-clear-filters"
          >
            {t('common.clear')}
          </Button>
        )}
        <div className="md:ml-auto text-[12px] text-muted-foreground">
          <span className="font-semibold text-foreground/80 num">{data.total}</span>{' '}
          {t('orders.title').toLowerCase()}
        </div>
      </div>

      <div className="premium-card overflow-hidden">
        {ordersQuery.isLoading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : data.items.length === 0 ? (
          <div className="p-6">
            {q ? (
              <div className="flex items-start gap-3 rounded-lg border p-4 max-w-2xl mx-auto"
                    style={{
                      borderColor: 'rgba(239,68,68,0.35)',
                      background: 'rgba(239,68,68,0.07)',
                    }}
                    data-testid="orders-not-found-panel">
                <CircleDashed className="mt-0.5 h-5 w-5 text-red-500 shrink-0" strokeWidth={1.5} />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-foreground text-[14px]">
                    {t('search.not_found_title').replace('{{q}}', q)}
                  </div>
                  <div className="text-[12px] text-muted-foreground mt-0.5">
                    {t('search.not_found_subtitle')}
                  </div>
                  {looksLikeOrderId && isAdmin && (
                    <Button
                      type="button"
                      size="sm"
                      className="mt-3"
                      onClick={handleSyncFromTikTok}
                      disabled={syncing}
                      data-testid="orders-sync-now-button"
                    >
                      {syncing
                        ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        : <RefreshCw className="h-4 w-4 mr-2" />}
                      {t('search.sync_from_tiktok')}
                    </Button>
                  )}
                </div>
              </div>
            ) : (
              <EmptyState message={t('common.empty')} testid="orders-empty-state" />
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table data-testid="orders-table">
              <TableHeader className="sticky top-0 z-10">
                <TableRow>
                  <TableHead className="min-w-[260px] whitespace-nowrap">{t('orders.column_order')}</TableHead>
                  <TableHead>{t('orders.column_auction')}</TableHead>
                  <TableHead>
                    <button type="button" onClick={() => toggleSort('date')}
                      className="inline-flex items-center gap-1 hover:text-foreground transition-colors"
                      data-testid="orders-sort-date">
                      {t('orders.column_date')}
                      {sortBy === 'date' && (sortDir === 'asc'
                        ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />)}
                    </button>
                  </TableHead>
                  <TableHead>
                    <button type="button" onClick={() => toggleSort('customer')}
                      className="inline-flex items-center gap-1 hover:text-foreground transition-colors"
                      data-testid="orders-sort-customer">
                      {t('orders.column_customer')}
                      {sortBy === 'customer' && (sortDir === 'asc'
                        ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />)}
                    </button>
                  </TableHead>
                  <TableHead className="text-right">
                    <button type="button" onClick={() => toggleSort('total')}
                      className="inline-flex items-center gap-1 hover:text-foreground transition-colors ml-auto"
                      data-testid="orders-sort-total">
                      {t('orders.column_total')}
                      {sortBy === 'total' && (sortDir === 'asc'
                        ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />)}
                    </button>
                  </TableHead>
                  <TableHead>
                    <button type="button" onClick={() => toggleSort('status')}
                      className="inline-flex items-center gap-1 hover:text-foreground transition-colors"
                      data-testid="orders-sort-status">
                      {t('orders.column_status')}
                      {sortBy === 'status' && (sortDir === 'asc'
                        ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />)}
                    </button>
                  </TableHead>
                  <TableHead>{t('orders.column_qbo')}</TableHead>
                  <TableHead className="text-right">{t('orders.column_actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((o) => {
                  const total = (o.raw?.items || []).reduce(
                    (s, i) => s + (i.unit_price || 0) * (i.quantity || 0), 0
                  );
                  return (
                    <TableRow
                      key={o.tiktok_order_id}
                      onClick={() => setSelected(o.tiktok_order_id)}
                      className="cursor-pointer"
                    >
                      <TableCell className="font-mono text-[13px] text-muted-foreground font-medium whitespace-nowrap">
                        <div
                          className="flex items-center gap-1.5"
                          data-testid={`orders-tiktok-id-${o.tiktok_order_id}`}
                        >
                          <span className="group-hover:text-[hsl(var(--primary))] transition-colors">
                            {o.tiktok_order_id}
                          </span>
                          <button
                            type="button"
                            className="shrink-0 h-6 w-6 inline-flex items-center justify-center rounded-md text-muted-foreground/50 hover:text-[hsl(var(--primary))] hover:bg-foreground/[0.06] transition-colors"
                            onClick={(e) => {
                              e.stopPropagation();
                              copyToClipboard(o.tiktok_order_id, t('orders.copied'));
                            }}
                            title={t('orders.copy_id')}
                            aria-label={t('orders.copy_id')}
                            data-testid={`orders-copy-id-${o.tiktok_order_id}`}
                          >
                            <Copy className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-[13px] text-muted-foreground" data-testid={`orders-auction-${o.tiktok_order_id}`}>
                        {o.raw?.items?.[0]?.sku || '—'}
                      </TableCell>
                      <TableCell className="font-mono text-muted-foreground/80">
                        {formatDate(o.raw?.created_at || o.created_at, locale)}
                      </TableCell>
                      <TableCell>
                        <div className="text-[13px] text-foreground font-medium">
                          {o.buyer_name_override || o.raw?.customer?.full_name || '—'}
                        </div>
                        <div className="text-[11px] text-muted-foreground/60 mt-0.5">
                          {o.raw?.customer?.email || ''}
                        </div>
                      </TableCell>
                      <TableCell className="text-right num text-[14px] font-bold text-foreground">
                        {formatCurrency(total, locale)}
                      </TableCell>
                      <TableCell><StatusBadge status={displayStatus(o)} /></TableCell>
                      <TableCell className="font-mono text-[12px] text-muted-foreground/60" data-testid={`orders-qbo-${o.tiktok_order_id}`}>
                        {o.invoice_doc_number ? (
                          <span
                            className="inline-flex items-center gap-1 text-[hsl(var(--primary))] font-medium"
                            title={t('orders.invoice_number')}
                            data-testid={`orders-qbo-invoice-${o.tiktok_order_id}`}
                          >
                            <Receipt className="h-3 w-3" />
                            {o.invoice_doc_number}
                          </span>
                        ) : (
                          <span
                            title={t('orders.detail_so')}
                            data-testid={`orders-qbo-estimate-${o.tiktok_order_id}`}
                          >
                            {shortId(o.sales_order_id)}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-[140ms]">
                          <button
                            type="button"
                            className="h-7 w-7 inline-flex items-center justify-center rounded-md text-muted-foreground hover:text-[hsl(var(--primary))] hover:bg-foreground/[0.05] transition-colors"
                            onClick={(e) => { e.stopPropagation(); setSelected(o.tiktok_order_id); }}
                            data-testid={`orders-view-${o.tiktok_order_id}`}
                            aria-label="Ver detalle"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          {(o.status === 'failed' || o.status === 'manual_review') && (
                            <Button
                              variant="secondary"
                              size="sm"
                              className="h-7 px-2 text-[11px]"
                              onClick={(e) => { e.stopPropagation(); retryMutation.mutate(o.tiktok_order_id); }}
                              disabled={retryMutation.isPending}
                              data-testid={`orders-retry-${o.tiktok_order_id}`}
                            >
                              {t('orders.retry')}
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}

        <div className="flex items-center justify-between px-6 py-3 border-t border-default">
          <span className="text-[12px] text-muted-foreground">
            {t('common.page_of').replace('{{page}}', page).replace('{{pages}}', totalPages)}
          </span>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
              data-testid="orders-pagination-prev"
            >
              {t('common.pagination_prev')}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              data-testid="orders-pagination-next"
            >
              {t('common.pagination_next')}
            </Button>
          </div>
        </div>
      </div>

      <Sheet open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-xl overflow-y-auto bg-background text-foreground"
          style={{ borderLeft: '1px solid var(--border-default)' }}
          data-testid="order-detail-drawer"
        >
          <SheetHeader>
            <SheetTitle>{t('orders.detail_title')}</SheetTitle>
            <SheetDescription className="font-mono text-[12px] text-muted-foreground">{selected}</SheetDescription>
          </SheetHeader>
          {detailQuery.isLoading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : detailQuery.data && (
            <div className="space-y-4 mt-6">
              <SectionBlock title={t('orders.detail_customer')}>
                <div className="text-sm">
                  <div className="font-medium text-foreground">
                    {detailQuery.data.order.raw?.customer?.full_name || '—'}
                  </div>
                  <div className="text-[12px] text-muted-foreground mt-0.5">
                    {detailQuery.data.order.raw?.customer?.email || ''}
                  </div>
                  <div className="text-[12px] text-muted-foreground">
                    {detailQuery.data.order.raw?.customer?.phone || ''}
                  </div>
                </div>
                <div className="mt-2 text-[11px] text-muted-foreground">
                  <span className="font-mono">qbo_customer_id:</span> {shortId(detailQuery.data.customer?.qbo_customer_id)}
                </div>
                <div className="text-[11px] text-muted-foreground">
                  dedup_action: <span className="font-mono">{detailQuery.data.order.dedup_action}</span>
                </div>
              </SectionBlock>

              <SectionBlock title="Items">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>SKU</TableHead>
                      <TableHead>Qty</TableHead>
                      <TableHead className="text-right">Unit</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(detailQuery.data.order.raw?.items || []).map((it, i) => (
                      <TableRow key={i}>
                        <TableCell className="font-mono text-xs">{it.sku}</TableCell>
                        <TableCell>{it.quantity}</TableCell>
                        <TableCell className="text-right num">
                          {formatCurrency(it.unit_price, locale)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </SectionBlock>

              <SectionBlock title={t('orders.detail_so')}>
                {detailQuery.data.sales_order ? (
                  <div className="text-xs space-y-1">
                    <div>id: <span className="font-mono">{shortId(detailQuery.data.sales_order.qbo_so_id)}</span></div>
                    <div>day_bucket: {detailQuery.data.sales_order.day_bucket}</div>
                    <div>total: {formatCurrency(detailQuery.data.sales_order.total, locale)}</div>
                    <div>orders: {detailQuery.data.sales_order.order_ids?.length}</div>
                  </div>
                ) : <div className="text-xs text-muted-foreground">{t('common.empty')}</div>}
              </SectionBlock>

              <SectionBlock title={t('orders.detail_invoice')}>
                {detailQuery.data.invoice ? (
                  <div className="text-xs space-y-1" data-testid="order-detail-invoice-info">
                    <div>
                      {t('orders.invoice_number')}:{' '}
                      <span className="font-mono font-semibold text-foreground" data-testid="order-detail-invoice-number">
                        {detailQuery.data.invoice.doc_number || '—'}
                      </span>
                    </div>
                    <div>
                      {t('orders.invoice_id')}:{' '}
                      <span className="font-mono">{shortId(detailQuery.data.invoice.qbo_invoice_id)}</span>
                    </div>
                    <div>
                      {t('common.status')}:{' '}
                      <StatusBadge status={detailQuery.data.invoice.status || 'waiting_label'} />
                    </div>
                    {detailQuery.data.invoice.carrier && (
                      <div>{t('orders.invoice_carrier')}: {detailQuery.data.invoice.carrier}</div>
                    )}
                  </div>
                ) : <div className="text-xs text-muted-foreground" data-testid="order-detail-invoice-empty">{t('common.empty')}</div>}
              </SectionBlock>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function SectionBlock({ title, children }) {
  return (
    <div
      className="rounded-xl p-4"
      style={{ background: 'var(--border-subtle)', border: '1px solid var(--border-default)' }}
    >
      <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground mb-2">
        {title}
      </div>
      {children}
    </div>
  );
}
